import React from 'react';
import { Check, Sparkles, Zap, Clock, TrendingUp, Gift } from 'lucide-react';
import { motion } from 'framer-motion';
import { formatPrice } from '../utils/pricing';

const PricingCard = ({
  tier,
  name,
  description,
  price = 0,
  originalPrice = 0,
  durationMonths = 1,
  discountPercent = 0,
  features,
  isPopular = false,
  currency = 'INR',
  onSelect,
  className = ""
}) => {
  const hasDiscount = originalPrice > 0 && originalPrice > price;
  const actualDiscount = hasDiscount ? Math.round((1 - price / originalPrice) * 100) : discountPercent;
  const perMonthPrice = durationMonths > 0 ? Math.round(price / durationMonths) : price;

  // Value messaging based on tier
  const getValueMessage = () => {
    if (tier === 'ultimate') return 'Best value! Only ₹250/month';
    if (tier === 'professional') return 'Most popular choice';
    if (tier === 'starter') return 'Great for trying out';
    return '';
  };

  // CTA text based on tier
  const getCTAText = () => {
    if (tier === 'ultimate') return 'UNLOCK FULL YEAR';
    if (tier === 'professional') return 'START NOW';
    if (tier === 'starter') return 'GET STARTED';
    return 'CURRENT PLAN';
  };

  // Duration label
  const getDurationLabel = () => {
    if (durationMonths === 1) return '1 month';
    if (durationMonths === 3) return '3 months';
    if (durationMonths === 12) return 'full year';
    return `${durationMonths} months`;
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -5 }}
      className={`relative bg-[#0F131F] border-2 rounded-3xl p-8 ${isPopular ? 'border-emerald-500 shadow-2xl shadow-emerald-500/20' :
          tier === 'ultimate' ? 'border-purple-500 shadow-xl shadow-purple-500/20' : 'border-white/10'
        } ${className}`}
    >
      {/* Popular Badge */}
      {isPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-2 rounded-full flex items-center space-x-2 shadow-lg">
            <TrendingUp className="w-4 h-4" />
            <span className="font-bold font-mono text-sm">MOST POPULAR</span>
          </div>
        </div>
      )}

      {/* Ultimate Best Value Badge */}
      {tier === 'ultimate' && !isPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-full flex items-center space-x-2 shadow-lg animate-pulse">
            <Gift className="w-4 h-4" />
            <span className="font-bold font-mono text-sm">BEST VALUE</span>
          </div>
        </div>
      )}

      {/* Discount Badge */}
      {hasDiscount && tier !== 'free' && actualDiscount > 0 && (
        <div className="absolute -top-3 -right-3">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-full shadow-lg"
          >
            <span className="font-bold font-mono text-sm">-{actualDiscount}% OFF</span>
          </motion.div>
        </div>
      )}

      {/* Corner Brackets */}
      <span className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-emerald-500/30" />
      <span className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-emerald-500/30" />
      <span className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-emerald-500/30" />
      <span className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-emerald-500/30" />

      {/* Header */}
      <div className="text-center mb-6 mt-4">
        <h3 className="text-2xl font-bold text-white mb-2 font-mono">{name}</h3>
        <p className="text-slate-400 text-sm font-mono">{description}</p>
      </div>

      {/* Price Section */}
      <div className="text-center mb-6">
        {/* Per Month Price - emphasized for longer durations */}
        {durationMonths > 1 && (
          <div className="mb-2">
            <span className="text-4xl font-bold text-emerald-400 font-mono">
              {formatPrice(perMonthPrice, currency)}
            </span>
            <span className="text-slate-400 text-lg ml-1 font-mono">/month</span>
          </div>
        )}

        {/* Original Price (Strikethrough) */}
        {hasDiscount && (
          <div className="mb-1">
            <span className="text-lg text-slate-500 line-through font-mono">
              {formatPrice(originalPrice, currency)}
            </span>
          </div>
        )}

        {/* Total Price */}
        <div className="flex items-baseline justify-center">
          <span className={`font-bold text-white font-mono ${durationMonths > 1 ? 'text-2xl' : 'text-5xl'}`}>
            {price === 0 ? 'Free' : formatPrice(price, currency)}
          </span>
          {durationMonths > 1 && price > 0 && (
            <span className="text-slate-400 ml-2 text-sm font-mono">
              total for {getDurationLabel()}
            </span>
          )}
        </div>

        {/* Duration label for 1 month */}
        {durationMonths === 1 && price > 0 && (
          <p className="text-sm text-slate-400 mt-2 font-mono">
            for {getDurationLabel()}
          </p>
        )}

        {/* Value Message */}
        {getValueMessage() && (
          <div className="mt-3">
            <span className={`text-xs font-bold font-mono px-3 py-1 rounded-full ${tier === 'ultimate' ? 'bg-purple-500/20 text-purple-400' :
                tier === 'professional' ? 'bg-emerald-500/20 text-emerald-400' :
                  'bg-slate-700 text-slate-300'
              }`}>
              {getValueMessage()}
            </span>
          </div>
        )}
      </div>

      {/* Features */}
      <div className="space-y-3 mb-6">
        {features.map((feature, index) => (
          <div key={index} className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center mt-0.5">
              <Check className="w-3 h-3 text-emerald-400" />
            </div>
            <span className="text-slate-300 text-sm font-mono">{feature}</span>
          </div>
        ))}
      </div>

      {/* Urgency for Ultimate */}
      {tier === 'ultimate' && (
        <div className="flex items-center justify-center gap-2 mb-4 text-amber-400 text-xs">
          <Clock className="w-3 h-3" />
          <span className="font-mono">Limited time offer</span>
        </div>
      )}

      {/* CTA Button */}
      <button
        onClick={onSelect}
        className={`w-full py-4 px-6 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 font-mono ${tier === 'ultimate'
            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:scale-105 shadow-lg shadow-purple-500/20'
            : isPopular
              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:scale-105 shadow-lg shadow-emerald-500/20'
              : tier === 'free'
                ? 'bg-slate-700 text-white hover:bg-slate-600'
                : 'bg-white/10 text-white hover:bg-white/20'
          }`}
      >
        <Zap className="w-5 h-5" />
        <span>{getCTAText()}</span>
      </button>

      {/* Money Back Guarantee */}
      {tier !== 'free' && (
        <p className="text-center text-xs text-slate-500 mt-3 font-mono">
          14-day money-back guarantee
        </p>
      )}
    </motion.div>
  );
};

export default PricingCard;

